import { useDispatch } from "react-redux";
import { useSendAudioMessageMutation } from "@store/companionApi";
import { setProcessing, setText } from "@store/avatarSlice";
import splitTextToSentences from "@utils/splitTextToSentences";
import { MutableRefObject, useCallback } from "react";
import { HeygenAvatarService } from "@service/avatar/heygen-avatar.service";
import { EventType } from '@models/audio-message';

const MAX_SENTENCE_LENGTH = 500;

const useBatching = ({
  sessionId,
  avatar,
}: {
  sessionId: string;
  avatar: MutableRefObject<HeygenAvatarService | null>;
}) => {
  const dispatch = useDispatch();
  const [sendAudioMessage] = useSendAudioMessageMutation();

  const sendMessage = async (
    audioBlob?: Blob | ArrayBufferLike,
    photoId?: string,
    eventType?: EventType,
  ): Promise<void> => {
    dispatch(setProcessing(true));
    try {
      const { data } = await sendAudioMessage({
        audio: (audioBlob as Blob) || null,
        sessionId,
        photoId: photoId || null,
        eventType: eventType || null,
      });

      if (!data) {
        return;
      }

      const { aiMessage } = data;

      if (!aiMessage) {
        return;
      }

      dispatch(setText(aiMessage));

      const sentences = splitTextToSentences(aiMessage, MAX_SENTENCE_LENGTH);

      await speakSentences(sentences);
    } catch (e) {
      console.error(e);
      dispatch(setProcessing(false));
    }
  };

  const speakSentences = useCallback(
    async (sentences: string[]) => {
      if (!avatar.current) {
        return;
      }
      for (const sentence of sentences) {
        await avatar.current.speak(sentence);
      }
    },
    [avatar],
  );

  return sendMessage;
};

export default useBatching;
